import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const MarketInsights: React.FC = () => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(contentRef, { once: false, amount: 0.2 });

    // Mock insights data
    const insights = [
        {
            id: 1,
            title: 'Bitcoin Forms Bullish Pattern After Recent Correction',
            category: 'Technical Analysis',
            date: 'Today',
            readTime: '3 min',
            important: true
        },
        {
            id: 2,
            title: 'Ethereum Layer 2 Solutions See Massive Adoption Growth',
            category: 'Fundamentals',
            date: 'Yesterday',
            readTime: '5 min',
            important: false
        }
    ];

    // Mock market data
    const marketData = [
        {
            name: 'Total Market Cap',
            value: '$1.84T',
            change: '+3.2%',
            positive: true
        },
        {
            name: 'BTC Dominance',
            value: '45.7%',
            change: '-0.5%',
            positive: false
        },
        {
            name: 'Fear & Greed',
            value: '65',
            change: '+8',
            positive: true,
            label: 'Greed'
        },
        {
            name: 'Active Addresses',
            value: '1.02M',
            change: '+12.3%',
            positive: true
        },
    ];

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.3,
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: [0.25, 0.25, 0, 1]
            }
        }
    };

    // Helper function for sentiment color
    const getSentimentColor = (value: string) => {
        const numericValue = parseInt(value);
        if (numericValue >= 60) return 'bg-green-500';
        if (numericValue >= 40) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    return (
        <section ref={sectionRef} id="market" className="section bg-secondary py-16 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 opacity-5 overflow-hidden">
                <div className="absolute top-0 left-0 w-1/2 h-1/2 rounded-full bg-accent/10 blur-3xl transform -translate-x-1/4 -translate-y-1/4"></div>
                <div className="absolute bottom-0 right-0 w-1/2 h-1/2 rounded-full bg-accent/10 blur-3xl transform translate-x-1/4 translate-y-1/4"></div>

                {/* Grid pattern */}
                <div className="absolute inset-0 grid grid-cols-8 grid-rows-8">
                    {Array.from({ length: 64 }).map((_, i) => (
                        <div key={i} className="border border-white/5"></div>
                    ))}
                </div>
            </div>

            <div className="container-custom relative z-10" ref={contentRef}>
                <div className="flex flex-col md:flex-row items-start justify-between gap-12">
                    <motion.div
                        className="w-full md:w-5/12 md:sticky md:top-32"
                        initial={{ opacity: 0, x: -50 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="mb-6 inline-block px-3 py-1 bg-accent/10 rounded-full text-accent text-sm font-medium">
                            Market Insights
                        </div>
                        <h2 className="heading-lg mb-6">Stay Ahead with <span className="text-accent">Data-driven</span> Insights</h2>
                        <p className="text-gray-400 mb-8">
                            Access powerful market analytics and curated insights to inform your trading decisions. Our platform aggregates and analyzes data from multiple sources to give you a competitive edge.
                        </p>

                        <div className="space-y-6 mb-8">
                            <motion.div
                                className="flex items-start gap-3"
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <div className="mt-1 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium">Technical Analysis</h3>
                                    <p className="text-gray-400 text-sm">Key indicators and chart patterns analyzed for you in real-time.</p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="flex items-start gap-3"
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <div className="mt-1 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium">Sentiment Analysis</h3>
                                    <p className="text-gray-400 text-sm">Gauge market mood with AI-powered social media and news analysis.</p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="flex items-start gap-3"
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                            >
                                <div className="mt-1 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium">Research Reports</h3>
                                    <p className="text-gray-400 text-sm">In-depth analysis from our team of crypto research analysts.</p>
                                </div>
                            </motion.div>
                        </div>

                        <motion.button
                            className="btn-primary mt-4"
                            whileHover={{
                                scale: 1.05,
                                boxShadow: '0 0 15px rgba(109, 40, 217, 0.5)'
                            }}
                            whileTap={{ scale: 0.95 }}
                        >
                            View All Insights
                        </motion.button>
                    </motion.div>

                    <div className="w-full md:w-7/12">
                        <motion.div
                            className="space-y-8"
                            variants={containerVariants}
                            initial="hidden"
                            animate={isInView ? "visible" : "hidden"}
                        >
                            {/* Featured Insights */}
                            <motion.div
                                className="space-y-4 mt-4"
                                variants={itemVariants}
                            >
                                {/* <h3 className="text-xl font-medium">Featured Insights</h3> */}

                                {insights.map((insight) => (
                                    <motion.div
                                        key={insight.id}
                                        className="card border border-white/5 p-5 hover:bg-secondary/90 cursor-pointer"
                                        whileHover={{
                                            x: 5,
                                            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)'
                                        }}
                                        whileTap={{ scale: 0.99 }}
                                    >
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="px-2 py-1 text-xs bg-accent/10 text-accent rounded-md">
                                                {insight.category}
                                            </div>
                                            {insight.important && (
                                                <motion.div
                                                    className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent"
                                                    animate={{
                                                        scale: [1, 1.15, 1],
                                                    }}
                                                    transition={{
                                                        duration: 2,
                                                        repeat: Infinity,
                                                        repeatType: "reverse"
                                                    }}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                                    </svg>
                                                </motion.div>
                                            )}
                                        </div>

                                        <h4 className="text-lg font-medium mb-3">{insight.title}</h4>

                                        <div className="flex justify-between items-center text-sm text-gray-400">
                                            <div>{insight.date}</div>
                                            <div className="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                </svg>
                                                {insight.readTime} read
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>

                            {/* Market Trend */}
                            <motion.div
                                className="card border border-white/5 p-5 bg-gradient-to-br from-secondary to-secondary/50"
                                variants={itemVariants}
                                whileHover={{
                                    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
                                }}
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="text-xl font-medium">Current Market Trend</h3>
                                    <div className="px-3 py-1 text-sm bg-green-500/10 text-green-400 rounded-full flex items-center">
                                        <span className="inline-block w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                                        Bullish
                                    </div>
                                </div>

                                <p className="text-gray-400 mb-5">
                                    The market is showing bullish signals based on on-chain metrics and technical indicators. Institutional inflows have increased by 24% in the past week.
                                </p>

                                {/* Trend chart placeholder */}
                                <div className="h-32 w-full bg-white/5 rounded-lg overflow-hidden">
                                    <svg viewBox="0 0 100 50" className="w-full h-full">
                                        <defs>
                                            <linearGradient id="trendGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                                <stop offset="0%" stopColor="#10B981" stopOpacity="0.2" />
                                                <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
                                            </linearGradient>
                                        </defs>

                                        {/* Trend line */}
                                        <motion.path
                                            d="M 0 35 C 10 32, 15 38, 20 28 S 30 18, 40 22 S 50 15, 60 12 S 70 5, 80 8 S 90 15, 100 10"
                                            fill="none"
                                            stroke="#10B981"
                                            strokeWidth="2"
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: 1 }}
                                            transition={{ duration: 1.5 }}
                                        />

                                        {/* Area under trend line */}
                                        <motion.path
                                            d="M 0 35 C 10 32, 15 38, 20 28 S 30 18, 40 22 S 50 15, 60 12 S 70 5, 80 8 S 90 15, 100 10 L 100 50 L 0 50 Z"
                                            fill="url(#trendGradient)"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 1, delay: 0.8 }}
                                        />
                                    </svg>
                                </div>

                                {/* Key metrics */}
                                <div className="grid grid-cols-3 gap-2 mt-5">
                                    <div className="text-center">
                                        <div className="text-xs text-gray-400">BTC Vol</div>
                                        <div className="text-sm font-medium text-white mt-1">$45.2B</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-xs text-gray-400">Avg. Gas</div>
                                        <div className="text-sm font-medium text-white mt-1">32 gwei</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-xs text-gray-400">Futures</div>
                                        <div className="text-sm font-medium text-green-400 mt-1">+0.02%</div>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MarketInsights; 